/**
 * Authentication Provider
 * Handles authentication state initialization, token refresh, and session management
 */

'use client';

import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/shared/providers/store';
import {
  initializeAuth,
  selectAuth,
  refreshTokenAsync,
  logoutAsync,
  getProfileAsync,
  clearAuthTokens
} from '@/shared/providers/state/authSlice';
import { appApi } from '@/infrastructure/api';

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, sessionToken, refreshToken: storedRefreshToken } = useAppSelector(selectAuth);

  // Initialize auth state on mount
  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  // Set up token refresh interval when authenticated
  useEffect(() => {
    if (!isAuthenticated || !storedRefreshToken) return;

    // Refresh token every 15 minutes (tokens typically last 1 hour)
    const refreshInterval = setInterval(async () => {
      try {
        await dispatch(refreshTokenAsync()).unwrap();
      } catch (error) {
        console.error('Token refresh failed:', error);
        // If refresh fails, logout the user
        dispatch(logoutAsync());
      }
    }, 15 * 60 * 1000); // 15 minutes

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, storedRefreshToken, dispatch]);

  // Load user profile when a session token is available (validate token)
  useEffect(() => {
    if (!sessionToken) return;

    // Set the auth token in the API client
    appApi.setAuthToken(sessionToken);

    // Load user profile; clear tokens if invalid
    dispatch(getProfileAsync())
      .unwrap()
      .catch(() => {
        dispatch(clearAuthTokens());
      });
  }, [sessionToken, dispatch]);

  // Set up session activity monitoring
  useEffect(() => {
    if (!isAuthenticated) return;

    let activityTimeout: NodeJS.Timeout;

    const resetActivityTimeout = () => {
      clearTimeout(activityTimeout);
      // Auto-logout after 8 hours of inactivity (configurable)
      activityTimeout = setTimeout(() => {
        console.log('Auto-logout due to inactivity');
        dispatch(logoutAsync());
      }, 8 * 60 * 60 * 1000); // 8 hours
    };

    const handleActivity = () => {
      resetActivityTimeout();
    };

    // Listen for user activity events
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    resetActivityTimeout();

    return () => {
      clearTimeout(activityTimeout);
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [isAuthenticated, dispatch]);

  return <>{children}</>;
};

export default AuthProvider;
